import { Checkbox, ImageViewer, Tag } from '@scalingworks/react-admin-ui';
import { createHelpers, createResource, ResourceField, sentenceCase } from '@scalingworks/refine-react-admin';

import { type Challenge, ChallengeSubmissionStatus, ChallengeTypeEnum } from '~/api';
import { ActionButton, TriggerConfirmModal } from '~/components';
import { useState } from 'react';
import { resourceNames } from '~/resources/resource-names';
import { useTranslate } from '@refinedev/core';
import { formatDateRange } from '~/resources/helpers';
import { ChallengeCreatePage } from './create';
import { ChallengeShowPage } from './show';
import { ChallengeEditPage } from './edit';
import { FiAward } from 'react-icons/fi';
import { ResourceContext } from '~/resources/type';
import { getChallengeStatus } from './helpers';
import { getSdk } from '~/api';

const { defineFields, defineShowPage } = createHelpers<Challenge>({
  resourceName: resourceNames.challenge,
});

export const challengeFields: ResourceField<Challenge>[] = [
  'id',
  'createdAt',
  'updatedAt',
  'title',
  'description',
  'startDate',
  'endDate',
  'enabled',
  'type',
  // 'url', removed
  'limitPerCustomer',
  { asset: ['id', 'source'] },
  // { challengePlatform: ['id', 'platformType'] }, removed
  {links: ['id', 'title', 'description', 'url', { platform: ['id', 'platformType']}]}, // new field
  { outlet: ['id', 'name'] },
  { submissions: ['id', 'status'] },
];

export const challengeResource = (context: ResourceContext) => {
  return createResource({
    name: resourceNames.challenge,
    label: 'Challenge',
    icon: <FiAward />,
    fields: defineFields(challengeFields),
    defaultValues: {} as any,
    defaultPageSize: 25,
    defaultSorter: [{ field: 'createdAt', order: 'desc' }],
    allowEdit: true,
    allowCreate: true,
    searchConfig: {
      placeholder: ({ t }) =>
        t('challenges.placeholder.search', { fallback: `Search by Challenge Title`, ns: 'common' }),
    },
    dataProvider: {
      create: async (params) => {
        const { variables, client } = params;
        const { links, ...challengeData } = variables as any;

        console.log('params ??', params)
        
        // Validate that we have at least one valid link
        const validLinks = links?.filter((link: any) => {
          const hasUrl = !!link.url;
          const hasPlatform = link.platformId && link.platformId.trim().length > 0;
          return hasUrl && hasPlatform;
        }) || [];
        
        if (validLinks.length === 0) {
          throw new Error('At least one challenge link with URL and platform is required');
        }
        
        try {
          // Step 1: Create the challenge
          const challengeResponse = await getSdk(client).createChallenge({
            input: challengeData
          });
          
          const createdChallenge = challengeResponse.createChallenge;
          
          // Step 2: Create challenge links
          const linkPromises = validLinks.map((link: any) => 
            getSdk(client).createChallengeLink({
              input: {
                challengeId: createdChallenge.id,
                title: link.title,
                description: link.description,
                url: link.url,
                platformId: link.platformId
              }
            })
          );
          
          // Wait for all links to be created
          await Promise.all(linkPromises);
          
          return {
            data: createdChallenge
          };
        } catch (error) {
          console.error('Error creating challenge:', error);
          throw error;
        }
      },
      update: async (params) => {
        const { variables, client, meta } = params;
        const { id, links, ...challengeData } = variables as any;
        const deletedLinkIds = meta?.deletedLinkIds || [];
        
        // Validate that we will have at least one valid link after all operations
        const validLinks = links?.filter((link: any) => {
          const hasUrl = !!link.url;
          const hasPlatform = link.platformId && link.platformId.trim().length > 0;
          return hasUrl && hasPlatform;
        }) || [];
        
        if (validLinks.length === 0) {
          throw new Error('At least one challenge link with URL and platform is required');
        }
        
        try {
          // Step 1: Update the challenge
          const challengeResponse = await getSdk(client).updateChallenge({
            input: {
              id,
              ...challengeData
            }
          });
          
          const updatedChallenge = challengeResponse.updateChallenge;
          
          // Step 2: Handle challenge links with full CRUD operations
          const existingLinks = validLinks.filter((link: any) => link.id);
          const newLinks = validLinks.filter((link: any) => !link.id);
          
          // Create new links
          if (newLinks.length > 0) {
            const createLinkPromises = newLinks.map((link: any) => 
              getSdk(client).createChallengeLink({
                input: {
                  challengeId: updatedChallenge.id,
                  title: link.title,
                  description: link.description,
                  url: link.url,
                  platformId: link.platformId
                }
              })
            );
            
            await Promise.all(createLinkPromises);
          }
          
          // Update existing links
          if (existingLinks.length > 0) {
            const updateLinkPromises = existingLinks.map((link: any) => 
              getSdk(client).updateChallengeLink({
                input: {
                  id: link.id,
                  challengeId: updatedChallenge.id,
                  title: link.title,
                  description: link.description,
                  url: link.url,
                  platformId: link.platformId
                }
              })
            );
            
            await Promise.all(updateLinkPromises);
          }
          
          // Step 3: Delete removed links
          if (deletedLinkIds.length > 0) {
            const deleteLinkPromises = deletedLinkIds.map((linkId: string) => 
              getSdk(client).deleteChallengeLink({ id: linkId })
            );
            
            await Promise.all(deleteLinkPromises);
          }
          
          return {
            data: updatedChallenge
          };
        } catch (error) {
          console.error('Error updating challenge:', error);
          throw error;
        }
      },
    },
    filterControls: {
      title: {
        type: 'text',
        operator: 'contains',
        config: {
          label: 'Title',
          placeholder: 'Filter by title...',
        },
      },
      description: {
        type: 'text',
        operator: 'contains',
        config: {
          label: 'Description',
          placeholder: 'Filter by description...',
        },
      },
      type: {
        type: 'select',
        operator: 'eq',
        config: {
          label: 'Challenge Type',
          placeholder: 'Filter by type...',
          options: Object.values(ChallengeTypeEnum).map(type => ({
            label: sentenceCase(type),
            value: type,
          })),
        },
      },
      startDate: {
        type: 'daterange',
        config: {
          label: 'Start Date',
        },
      },
      endDate: {
        type: 'daterange',
        config: {
          label: 'End Date',
        },
      },
    },
    createConfig: {
      title: ({ t }) => t('challenges.create.name', { fallback: `Create Challenge`, ns: 'common' }),
    },
    filterConfig: {
      alwaysExpanded: true,
    },
    allowDelete: true,
    columns: ({ LinkToDetails, navigateToEdit, invokeDelete, t }) => [
      {
        id: 'title',
        header: t('challenges.column.title', { fallback: 'Title', ns: 'common' }),
        cell: (data) => {
          const { id, title } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <span>{title}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'description',
        header: t('challenges.column.description', { fallback: 'Description', ns: 'common' }),
        cell: (data) => {
          const { id, description } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <span>{description}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'period',
        header: t('challenges.column.period', { fallback: 'Period', ns: 'common' }),
        cell: (data) => {
          const { id, startDate, endDate } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <span>{formatDateRange(startDate, endDate)}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'status',
        header: t('challenges.column.status', { fallback: 'Status', ns: 'common' }),
        cell: (data) => {
          const { id, startDate, endDate } = data.row.original;
          
          const { label, color } = getChallengeStatus(startDate, endDate);
          
          return (
            <LinkToDetails resourceId={id}>
              <Tag color={color}>{label}</Tag>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'enabled',
        header: t('challenges.column.enabled', { fallback: 'Enabled', ns: 'common' }),
        cell: (data) => {
          const { id, enabled } = data.row.original;
          return (
            <LinkToDetails resourceId={id}>
              <Checkbox checked={enabled} />
            </LinkToDetails>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div />,
        accessorKey: 'id',
        enableSorting: false,
        cell: (data) => {
          const { id: challengeId, title } = data.row.original;
          const [showDel, setShowDel] = useState(false);

          return (
            <>
              <ActionButton
                actions={[
                  {
                    label: t('actions.edit', { fallback: 'Edit', ns: 'common' }),
                    name: 'edit',
                    onAction: () => navigateToEdit({ id: challengeId }),
                  },
                  // {
                  //   label: t('actions.delete', { fallback: 'Delete', ns: 'common' }),
                  //   name: 'delete',
                  //   onAction: () => invokeDelete({ id: challengeId }),
                  //   render: (onAction) => {
                  //     return (
                  //       <button type="button">
                  //         <TriggerConfirmModal
                  //           title={t('challenges.delete', { fallback: `Delete ${title}`, ns: 'common' })}
                  //           visible={showDel}
                  //           onOpenChange={setShowDel}
                  //           onPressConfirm={onAction}
                  //           description={
                  //             <span>
                  //               {t('warnings.deleteConfirmation', {
                  //                 fallback: 'Are you sure you want to delete',
                  //                 ns: 'common',
                  //               })}
                  //               <span className="font-semibold text-error-300">{` ${title}`}</span>
                  //               {'? '}
                  //               {t('warnings.cannotUndo', { fallback: 'This action cannot be undone', ns: 'common' })}
                  //             </span>
                  //           }
                  //         />
                  //       </button>
                  //     );
                  //   },
                  // },
                ]}
              />
            </>
          );
        },
      },
    ],
    show: defineShowPage({
      component: (props) => {
        return <ChallengeShowPage queryResult={props.queryResult} />;
      },
    }),
    edit: {
      render: (helpers) => {
        return <ChallengeEditPage />;
      },
    },
    create: {
      render: (helpers) => {
        return <ChallengeCreatePage />;
      },
    },
  });
}; 